/** Forms: submissions inbox + form definitions (posted to /__yc/forms/<key>). Route /sites/:id/forms */
import React, { useEffect, useState } from 'react';
import {
    Badge, Button, Card, Checkbox, Collapse, Descriptions, Drawer, Empty, Input, InputNumber, Popconfirm, Select, Space, Table, Tabs, Typography, message,
} from 'antd';
import { DeleteOutlined, DownloadOutlined, EditOutlined, MailOutlined, PaperClipOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { CopyLine, editorPath, fmtDate, listOf, toastErr, totalOf, useSite } from './common';

const { Text } = Typography;

const Inbox = ({ forms, onRead }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [form, setForm] = useState('');
    const [unread, setUnread] = useState(false);
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const d = await api.get('/form-submissions', { form: form || undefined, unread: unread ? 1 : undefined, page, per_page: 30 });
            setData({ items: listOf(d), total: totalOf(d) });
        } catch (e) { toastErr(e); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [form, unread, page]); // eslint-disable-line react-hooks/exhaustive-deps

    const view = async (s) => {
        setOpen(s);
        if (!s.read_at) {
            try {
                await api.post(`/form-submissions/${s.id}/read`);
                setData((d) => ({ ...d, items: d.items.map((x) => (x.id === s.id ? { ...x, read_at: new Date().toISOString() } : x)) }));
                onRead?.();
            } catch (_) { /* ignore */ }
        }
    };
    const del = async (s) => {
        try { await api.del(`/form-submissions/${s.id}`); setOpen(null); load(); } catch (e) { toastErr(e); }
    };
    const preview = (s) => {
        const f = s.fields || {};
        const first = f.email || f.name || Object.values(f).find((v) => typeof v === 'string');
        const msg = f.message || f.body || Object.values(f).filter((v) => typeof v === 'string').slice(1, 2)[0];
        return <span><Text strong={!s.read_at}>{String(first || '-')}</Text>{msg ? <Text type="secondary"> — {String(msg).slice(0, 90)}</Text> : null}</span>;
    };
    const formName = (k) => forms.find((f) => f.form_key === k)?.name || k;

    return (
        <>
            <Space wrap style={{ marginBottom: 12 }}>
                <Select style={{ width: 200 }} value={form} onChange={(v) => { setForm(v); setPage(1); }}
                    options={[{ value: '', label: t('All forms') }, ...forms.map((f) => ({ value: f.form_key, label: <span>{f.name} {f.unread ? <Badge count={f.unread} size="small" /> : null}</span> }))]} />
                <Checkbox checked={unread} onChange={(e) => { setUnread(e.target.checked); setPage(1); }}>{t('Unread only')}</Checkbox>
                <Button type="text" icon={<ReloadOutlined />} onClick={load} />
            </Space>
            <Table rowKey="id" size="small" loading={loading} dataSource={data.items} scroll={{ x: 600 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No submissions yet')} /> }}
                onRow={(s) => ({ onClick: () => view(s), style: { cursor: 'pointer' } })}
                pagination={{ current: page, pageSize: 30, total: data.total, onChange: setPage, hideOnSinglePage: true, showSizeChanger: false }}
                columns={[
                    { title: '', key: 'u', width: 24, render: (_, s) => (s.read_at ? null : <Badge status="processing" />) },
                    { title: t('Submission'), key: 'p', render: (_, s) => preview(s) },
                    { title: t('Form'), dataIndex: 'form_key', width: 160, render: formName },
                    { title: t('Received', { context: 'shop' }), dataIndex: 'submitted_at', width: 170, render: (v, s) => fmtDate(v || s.created_at) },
                ]} />
            <Drawer open={!!open} onClose={() => setOpen(null)} title={open ? formName(open.form_key) : ''} width={Math.min(520, window.innerWidth)}
                extra={open && (
                    <Space>
                        {open.fields?.email && <Button icon={<MailOutlined />} href={`mailto:${open.fields.email}`}>{t('Reply')}</Button>}
                        <Popconfirm title={t('Delete?')} onConfirm={() => del(open)}><Button danger icon={<DeleteOutlined />} /></Popconfirm>
                    </Space>
                )}>
                {open && (
                    <>
                        <Descriptions column={1} size="small" bordered>
                            {Object.entries(open.fields || {}).filter(([k]) => !(open.files || []).some((x) => x.field === k)).map(([k, v]) => (
                                <Descriptions.Item key={k} label={forms.find((x) => x.form_key === open.form_key)?.fields?.find((x) => x.name === k)?.label || k}><span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{Array.isArray(v) ? v.join(', ') : String(v ?? '')}</span></Descriptions.Item>
                            ))}
                        </Descriptions>
                        {(open.files || []).map((x) => (
                            <div key={x.name} style={{ marginTop: 8 }}><PaperClipOutlined /> <a href={x.url} target="_blank" rel="noopener noreferrer">{x.name}</a> <Text type="secondary">({Math.ceil(x.bytes / 1024)} KB)</Text></div>
                        ))}
                        <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
                            {fmtDate(open.submitted_at || open.created_at)} · {open.page || '-'} · {open.ip}
                        </Text>
                    </>
                )}
            </Drawer>
        </>
    );
};

const CAPTCHA = ['off', 'auto', 'always'];
const lines = (s) => String(s || '').split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);

/** One form: fields come from the page (read-only here), the rest are its settings. */
const FormSettings = ({ form, onSaved }) => {
    const { t } = useTranslation();
    const { api, siteId } = useSite();
    const [st, setSt] = useState(form.settings);
    const [name, setName] = useState(form.name);
    const [recipients, setRecipients] = useState((form.settings?.recipients?.length ? form.settings.recipients : (form.notify_email ? [form.notify_email] : [])).join('\n'));
    const [saving, setSaving] = useState(false);
    const [log, setLog] = useState(null);
    useEffect(() => { setSt(form.settings); setName(form.name); }, [form]);
    const set = (patch) => setSt((x) => ({ ...x, ...patch }));
    const rules = st.routing?.rules || [];
    const setRules = (r) => set({ routing: { ...st.routing, rules: r } });
    const fieldOpts = (form.fields || []).filter((f) => ['select', 'radio'].includes(f.type));
    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/forms/${form.form_key}/settings`, { ...st, name, recipients: lines(recipients) });
            message.success(st.captcha !== form.settings?.captcha ? t('Saved — publish to apply') : t('Saved'));
            onSaved();
        } catch (e) { toastErr(e); } finally { setSaving(false); }
    };
    const placeholders = (form.fields || []).filter((f) => !['textarea', 'file', 'hidden'].includes(f.type)).map((f) => `{{${f.name}}}`).join(' ');
    return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Table size="small" pagination={false} rowKey="name" dataSource={form.fields || []} scroll={{ x: 480 }}
                columns={[
                    { title: t('Label'), dataIndex: 'label' },
                    { title: t('Field'), dataIndex: 'name', width: 150 },
                    { title: t('Type'), dataIndex: 'type', width: 100 },
                    { title: t('Required'), dataIndex: 'required', width: 80, align: 'center', render: (v) => (v ? '✓' : '') },
                    { title: t('Options'), dataIndex: 'options', render: (v) => (v || []).join(', ') },
                ]} />
            <Button icon={<EditOutlined />} href={editorPath(siteId)}>{t('Edit on the page')}</Button>
            <Input addonBefore={t('Name')} value={name} onChange={(e) => setName(e.target.value)} style={{ maxWidth: 360 }} />
            <div>
                <Text strong>{t('Send notifications to')}</Text>
                <Input.TextArea rows={2} placeholder="you@example.com" value={recipients} onChange={(e) => setRecipients(e.target.value)} />
            </div>
            <div>
                <Text strong>{t('Route by answer')}</Text>
                <Space wrap style={{ display: 'flex', marginTop: 4 }}>
                    <Select allowClear style={{ width: 200 }} placeholder={t('Field')} value={st.routing?.field || undefined}
                        onChange={(v) => set({ routing: { ...st.routing, field: v || '', rules } })}
                        options={fieldOpts.map((f) => ({ value: f.name, label: f.label }))} />
                </Space>
                {st.routing?.field && rules.map((r, i) => (
                    <Space key={i} style={{ display: 'flex', marginTop: 6 }}>
                        <Input placeholder={t('Answer')} value={r.value} onChange={(e) => setRules(rules.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
                        <Input placeholder="team@example.com" value={r.email} onChange={(e) => setRules(rules.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))} />
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => setRules(rules.filter((_, j) => j !== i))} />
                    </Space>
                ))}
                {st.routing?.field && <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginTop: 6 }} onClick={() => setRules([...rules, { value: '', email: '' }])}>{t('Add rule')}</Button>}
            </div>
            <div>
                <Checkbox checked={!!st.autoreply?.enabled} onChange={(e) => set({ autoreply: { ...st.autoreply, enabled: e.target.checked } })}><Text strong>{t('Auto-reply to the sender')}</Text></Checkbox>
                {st.autoreply?.enabled && (
                    <Space direction="vertical" style={{ width: '100%', marginTop: 6 }}>
                        <Input placeholder={t('Subject')} value={st.autoreply.subject} onChange={(e) => set({ autoreply: { ...st.autoreply, subject: e.target.value } })} />
                        <Input.TextArea rows={4} placeholder={t('Message')} value={st.autoreply.body} onChange={(e) => set({ autoreply: { ...st.autoreply, body: e.target.value } })} />
                        <Text type="secondary" style={{ fontSize: 12 }}>{placeholders}</Text>
                    </Space>
                )}
            </div>
            <Space wrap>
                <span><Text strong>{t('Captcha')} </Text><Select style={{ width: 200 }} value={st.captcha} onChange={(captcha) => set({ captcha })}
                    options={CAPTCHA.map((c) => ({ value: c, label: { off: t('Off'), auto: t('When traffic is abnormal'), always: t('Always') }[c] }))} /></span>
                <span><Text strong>{t('Delete after (days)')} </Text><InputNumber min={0} max={3650} value={st.retention_days} onChange={(v) => set({ retention_days: v || 0 })} /></span>
            </Space>
            <div>
                <Text strong>{t('Webhook')}</Text>
                <Input placeholder="https://example.com/hook" value={st.webhook?.url} onChange={(e) => set({ webhook: { ...st.webhook, url: e.target.value } })} />
                {st.webhook?.secret && <Text type="secondary" style={{ fontSize: 12 }}>{t('Signing secret')}: <CopyLine value={st.webhook.secret} /></Text>}
                {st.webhook?.url && <Button size="small" type="link" onClick={() => api.get(`/forms/${form.form_key}/webhooks`).then((d) => setLog(listOf(d))).catch(toastErr)}>{t('Delivery log')}</Button>}
                {log && (
                    <Table size="small" pagination={false} rowKey="id" dataSource={log} scroll={{ x: 480 }} columns={[
                        { title: t('Received', { context: 'shop' }), dataIndex: 'created_at', render: (v) => fmtDate(v) },
                        { title: t('Status'), dataIndex: 'status' },
                        { title: t('Attempts'), dataIndex: 'attempts', width: 80 },
                        { title: 'HTTP', dataIndex: 'last_code', width: 70 },
                        { title: t('Error'), dataIndex: 'last_error' },
                    ]} />
                )}
            </div>
            <Space>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>{t('Save')}</Button>
                <Button icon={<DownloadOutlined />} onClick={() => api.download(`/forms/${form.form_key}/export.csv`, `${form.form_key}.csv`).catch(toastErr)}>{t('Export CSV')}</Button>
            </Space>
        </Space>
    );
};

const FormDefs = ({ forms, onSaved }) => {
    const { t } = useTranslation();
    if (!forms.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No forms yet')} />;
    return (
        <Collapse items={forms.map((f) => ({
            key: f.form_key,
            label: <Space><Text strong>{f.name}</Text><Text type="secondary" code>{f.form_key}</Text></Space>,
            children: <FormSettings form={f} onSaved={onSaved} />,
        }))} />
    );
};

const Forms = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [forms, setForms] = useState([]);
    const load = () => api.get('/forms').then((d) => setForms(listOf(d))).catch(toastErr);
    useEffect(() => { load(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
    const unread = forms.reduce((a, f) => a + (f.unread || 0), 0);
    return (
        <Card size="small">
            <Tabs items={[
                { key: 'inbox', label: <Badge count={unread} size="small" offset={[8, 0]}>{t('Inbox', { context: 'shop' })}</Badge>, children: <Inbox forms={forms} onRead={load} /> },
                { key: 'forms', label: t('Forms'), children: <FormDefs forms={forms} onSaved={load} /> },
            ]} />
        </Card>
    );
};

export default Forms;
